import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { eventsApi } from "../api";
import { useAuth } from "../context/AuthContext";
import { queryKeys } from "../queryKeys";
import { clearLastEventId } from "../routes/workspaceNavigation";
import type { DashboardData } from "../types";
import type { WorkspaceId } from "../utils/workspaceStorage";

/**
 * Shared event-dashboard fetch for the Overview and P&L tabs.
 *
 * Extracted when those two views were split out of DashboardPage so both
 * read the same cache entry — a single request serves whichever tab is open,
 * and switching between them is instant.
 *
 * Polls every 10s while the tab is visible so linked zoniq.in sales appear
 * without a full page reload.
 */
export function useEventDashboard(workspace: WorkspaceId, eventId: string | undefined) {
  const { token } = useAuth();

  const query = useQuery({
    queryKey:
      eventId && token
        ? queryKeys.events.dashboard(workspace, eventId)
        : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.dashboard(eventId!, token!),
    enabled: Boolean(token && eventId),
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // A 404 means the sticky lastEventId points at a deleted or inaccessible
  // event. Drop it so the next visit re-picks rather than looping back here.
  useEffect(() => {
    if (!query.isError) return;
    const message = query.error instanceof Error ? query.error.message : String(query.error ?? "");
    if (/404|not\s*found/i.test(message)) {
      clearLastEventId(workspace);
    }
  }, [query.error, query.isError, workspace]);

  const data: DashboardData | undefined = query.data;
  const errorMessage =
    query.error instanceof Error ? query.error.message : query.error ? String(query.error) : "";

  return { query, data, errorMessage, token };
}
